import { useAuthentication } from '@pflegenavi/frontend/authentication';
import type { ApiProviderProps } from '@pflegenavi/shared-frontend/platform';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  CountryDto,
  ExitResidentDto,
  NursingHomeDto,
  PutResidentServiceProvidersDto,
  RecurringItemTaxCodeDto,
  RecurringItemV2Dto,
  RecurringItemV2WithResidentV2Dto,
  RecurringItemWithResidentsV2Dto,
  Resident,
  ResidentConfiguration,
  ResidentListItemDto,
  ServiceProviderListDto,
  SettlementDto,
  SettlementFormDataDto,
  UpdateNursingHomeDto,
  UpdateNursingHomePaymentSettingsDto,
  UpdateResidentCashPayerStateDto,
  UpdateResidentConfigurationDto,
  UpdateResidentDto,
} from '@pflegenavi/shared/api';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  replaceEqualDeep,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import isEqual from 'lodash.isequal';
import type { FC } from 'react';
import { createContext, useCallback } from 'react';
import { invalidateCashList } from '../cash-management/queryKeys';
import { CASH_LISTS_PHOENIX_KEY } from '../cash-management-phoenix/queryKeys';
import { invalidateAllResidentBalance } from '../reporting/queryKeys';
import { SHOW_RESIDENT_KEY } from '../resident-phoenix/queryKeys';
import { TRANSACTIONS_PHOENIX_KEY } from '../transaction-phoenix/queryKeys';
import { RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY } from '../receipt-phoenix/queryKeys';
import {
  getTransactionsKey,
  TRANSACTIONS_PAGINATED_KEY,
} from '../transaction/queryKeys';
import { USER_PROFILE_KEY } from '../user-profile/queryKeys';
import type { IResidentApi } from './api';
import { ResidentApi } from './api';
import {
  ADD_RESIDENT_RECURRING_ITEM_KEY,
  ARCHIVE_RESIDENT_KEY,
  EXIT_RESIDENT_KEY,
  GET_COUNTRIES_KEY,
  GET_RECURRING_ITEM_RESIDENTS_KEY,
  GET_RECURRING_ITEMS,
  GET_RECURRING_ITEMS_BY_RESIDENT_ID_KEY,
  GET_RECURRING_ITEMS_KEY,
  GET_TAX_CODES_BY_COUNTRY,
  getResidentConfigurationKey,
  invalidateResident,
  invalidateResidentsKeys,
  NURSING_HOME_KEY,
  PUT_RESIDENT_SERVICE_PROVIDERS_LIST_KEY,
  REMOVE_RECURRING_ITEM_KEY,
  RESIDENT_SERVICE_PROVIDERS_LIST_KEY,
  RESIDENT_SETTLEMENT_KEY,
  RESIDENTS_KEY,
  UPDATE_RESIDENT_CASH_PAYER_STATUS_KEY,
  UPDATE_RESIDENT_CONFIGURATION_KEY,
  UPDATE_RESIDENT_KEY,
  UPDATE_RESIDENT_START_DATE_KEY,
} from './queryKeys';

export {
  isDuplicateResidentAccountingIdError,
  isStartDateAfterExitDateError,
} from './api';
export type { IResidentApi } from './api';

const ApiContext = createContext<IResidentApi | undefined>(undefined);
const { useApi: useResidentApi, ApiProvider } = makeApiProvider({
  name: 'Resident',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => new ResidentApi(tenantId, auth, apiUrl),
});

const ResidentApiProvider: FC<ApiProviderProps> = ({ children, ...props }) => {
  return <ApiProvider {...props}>{children}</ApiProvider>;
};

export {
  ApiContext as ResidentApiContext,
  ResidentApiProvider,
  useResidentApi,
};

const selectResidents = (
  data: ResidentListItemDto[]
): ResidentListItemDto[] => {
  return data.map((resident) => ({
    ...resident,
    threshold_due_date:
      resident.threshold_due_date && new Date(resident.threshold_due_date),
    negative_due_date:
      resident.negative_due_date && new Date(resident.negative_due_date),
    entry_date: resident.entry_date && new Date(resident.entry_date),
    exit_date: resident.exit_date && new Date(resident.exit_date),
    settlement_date:
      resident.settlement_date && new Date(resident.settlement_date),
  }));
};

const RESIDENT_STALE_TIME = 1000 * 60 * 5; // 5 minutes

// @deprecated. Only used for for Stripe tenants
/**
 * Heavily based on useResidents. Since we need to add additionalFields without breaking other queries
 * @param nursingHomeId
 * @param options
 */
export const useAllResidents = (
  nursingHomeId: string | undefined,
  options?: Omit<
    UseQueryOptions<ResidentListItemDto[], unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ResidentListItemDto[], unknown> => {
  const result = useApiQuery(
    useResidentApi,
    RESIDENTS_KEY(
      nursingHomeId,
      {},
      {
        additionalFields: {
          residentPaymentInfo: true,
        },
      }
    ),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.get(nursingHomeId, [], { residentPaymentInfo: true });
    },
    {
      enabled: nursingHomeId !== undefined,
      staleTime: RESIDENT_STALE_TIME,
      ...options,
      select: selectResidents,
    }
  );

  return result;
};

// @deprecated. Only used for for testing modal
export const useResidents = (
  nursingHomeId: string | undefined,
  options?: Omit<
    UseQueryOptions<ResidentListItemDto[], unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ResidentListItemDto[], unknown> => {
  const result = useApiQuery(
    useResidentApi,
    RESIDENTS_KEY(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.get(nursingHomeId, []);
    },
    {
      enabled: nursingHomeId !== undefined,
      staleTime: RESIDENT_STALE_TIME,
      ...options,
      select: selectResidents,
    }
  );

  return result;
};

export const useUpdateResident = (): UseMutationResult<
  Resident,
  unknown,
  UpdateResidentDto
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<Resident, unknown, UpdateResidentDto>({
    mutationKey: UPDATE_RESIDENT_KEY,
    mutationFn: (data) => api.update(data),
    onSuccess: (_result, data) => {
      return Promise.all([
        invalidateResidentsKeys(queryClient, _result.nursingHome.id),
        invalidateResident(
          queryClient,
          data.residentId,
          _result.nursingHome.id
        ),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(data.residentId),
        }),
      ]);
    },
  });
};

export const useRecurringItems = (
  nursingHomeId: string | undefined
): UseQueryResult<RecurringItemV2Dto[], unknown> => {
  return useApiQuery(
    useResidentApi,
    GET_RECURRING_ITEMS_KEY(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.getRecurringItems({ params: { nursingHomeId } });
    },
    {
      enabled: nursingHomeId !== undefined,
    }
  );
};

export const useRecurringItemResidents = (
  recurringItemId: string
): UseQueryResult<RecurringItemWithResidentsV2Dto, unknown> => {
  const select = useCallback((result: RecurringItemWithResidentsV2Dto) => {
    return {
      ...result,
      residents: result.residents.map((row: any) => ({
        ...row,
        startDate: parseISO(row.startDate),
        endDate: parseISO(row.endDate),
        entryDate: parseISO(row.entryDate),
      })),
    };
  }, []);

  return useApiQuery(
    useResidentApi,
    GET_RECURRING_ITEM_RESIDENTS_KEY(recurringItemId),
    (api) => api.getRecurringItemResidents({ params: { recurringItemId } }),
    {
      refetchOnWindowFocus: false,
      select,
    }
  );
};

export const useRecurringItemsByResident = (
  residentId: string
): UseQueryResult<RecurringItemV2WithResidentV2Dto[], unknown> => {
  return useApiQuery(
    useResidentApi,
    GET_RECURRING_ITEMS_BY_RESIDENT_ID_KEY(residentId),
    (api) => api.getRecurringItemsByResident({ params: { residentId } }),
    {
      structuralSharing: (oldData, newData) =>
        isEqual(oldData, newData)
          ? oldData
          : replaceEqualDeep(oldData, newData),
    }
  );
};

export const useRecurringItemFromList = (
  id: string,
  nursingHomeId: string | undefined
): UseQueryResult<RecurringItemV2Dto | undefined, unknown> => {
  const select = useCallback(
    (data: RecurringItemV2Dto[]) => {
      return data.find((row) => row.id === id);
    },
    [id]
  );
  return useApiQuery(
    useResidentApi,
    GET_RECURRING_ITEMS_KEY(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.getRecurringItems({ params: { nursingHomeId } });
    },
    {
      select,
      enabled: nursingHomeId !== undefined,
      refetchOnWindowFocus: false,
    }
  );
};

export const useDeleteRecurringItem = (): UseMutationResult<
  {
    success: boolean;
  },
  unknown,
  {
    recurringItemId: string;
    nursingHomeId: string;
  }
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      success: boolean;
    },
    unknown,
    {
      recurringItemId: string;
      nursingHomeId: string;
    }
  >({
    mutationKey: [REMOVE_RECURRING_ITEM_KEY],
    mutationFn: (data) =>
      api.deleteRecurringItem({
        params: { recurringItemId: data.recurringItemId },
      }),
    onSuccess: async (_data, variables) => {
      return await queryClient.invalidateQueries({
        queryKey: GET_RECURRING_ITEMS_KEY(variables.nursingHomeId),
      });
    },
  });
};

export const useUpdateResidentStartDate = (): UseMutationResult<
  {
    success: boolean;
  },
  unknown,
  {
    recurringItemId: string;
    residentId: string;
    startDate: Date;
  }
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      success: boolean;
    },
    unknown,
    {
      recurringItemId: string;
      residentId: string;
      startDate: Date;
    }
  >({
    mutationKey: [UPDATE_RESIDENT_START_DATE_KEY],
    mutationFn: (data) =>
      api.updateResidentStartDate({
        params: {
          recurringItemId: data.recurringItemId,
          residentId: data.residentId,
        },
        body: {
          startDate: data.startDate,
        },
      }),
    onSuccess: (_data, props) => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: GET_RECURRING_ITEMS }),
        queryClient.invalidateQueries({
          queryKey: GET_RECURRING_ITEM_RESIDENTS_KEY(props.recurringItemId),
        }),
        queryClient.invalidateQueries({
          queryKey: GET_RECURRING_ITEMS_BY_RESIDENT_ID_KEY(props.residentId),
        }),
      ]);
    },
  });
};

export const useAddResidentToRecurringItem = (): UseMutationResult<
  {
    success: boolean;
  },
  unknown,
  {
    recurringItemId: string;
    residentId: string;
    startDate: Date;
    endDate?: Date;
  }
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      success: boolean;
    },
    unknown,
    {
      recurringItemId: string;
      residentId: string;
      startDate: Date;
      endDate?: Date;
    }
  >({
    mutationKey: [ADD_RESIDENT_RECURRING_ITEM_KEY],
    mutationFn: (data) =>
      api.addResidentToRecurringItem({
        params: {
          recurringItemId: data.recurringItemId,
        },
        body: {
          residentId: data.residentId,
          startDate: data.startDate,
          endDate: data.endDate,
        },
      }),
    onSuccess: async (_data, props) => {
      await queryClient.invalidateQueries({ queryKey: GET_RECURRING_ITEMS });
      await queryClient.invalidateQueries({
        queryKey: GET_RECURRING_ITEM_RESIDENTS_KEY(props.recurringItemId),
      });
    },
  });
};

export const useGetCountries = (
  options?: Omit<
    UseQueryOptions<
      {
        data: CountryDto[];
      },
      unknown
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  {
    data: CountryDto[];
  },
  unknown
> => {
  return useApiQuery(
    useResidentApi,
    GET_COUNTRIES_KEY,
    (api) => {
      return api.getCountries();
    },
    options
  );
};

export const useGetTaxCodesByCountry = (
  countryId: string | undefined
): UseQueryResult<
  {
    data: RecurringItemTaxCodeDto[];
  },
  unknown
> => {
  return useApiQuery(
    useResidentApi,
    GET_TAX_CODES_BY_COUNTRY(countryId),
    (api) => {
      if (!countryId) {
        throw new Error('countryId is required');
      }
      return api.getTaxCodesByCountry({ params: { countryId } });
    },
    {
      enabled: countryId !== undefined,
    }
  );
};

export const useResidentConfiguration = (
  id: string
): UseQueryResult<ResidentConfiguration | undefined, unknown> => {
  return useApiQuery(useResidentApi, getResidentConfigurationKey(id), (api) =>
    api.getConfiguration(id)
  );
};

export const useUpdateResidentConfiguration = (): UseMutationResult<
  ResidentConfiguration,
  unknown,
  UpdateResidentConfigurationDto
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<
    ResidentConfiguration,
    unknown,
    UpdateResidentConfigurationDto
  >({
    mutationKey: UPDATE_RESIDENT_CONFIGURATION_KEY,
    mutationFn: (data) => api.updateConfiguration(data),
    onSuccess: (_result, data) => {
      return Promise.all([
        invalidateResident(
          queryClient,
          data.resident_id,
          _result.nursingHomeId
        ),
        queryClient.invalidateQueries({
          queryKey: getResidentConfigurationKey(data.resident_id),
        }),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(data.resident_id),
        }),
      ]);
    },
  });
};

export const useUpdateResidentCashPayerStatus = (
  residentId: string,
  nursingHomeId?: string
): UseMutationResult<
  {
    success: boolean;
  },
  unknown,
  UpdateResidentCashPayerStateDto
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      success: boolean;
    },
    unknown,
    UpdateResidentCashPayerStateDto
  >({
    mutationKey: [UPDATE_RESIDENT_CASH_PAYER_STATUS_KEY],
    mutationFn: (data) => {
      if (!nursingHomeId) {
        throw Error('nursingHomeId is undefined');
      }
      return api.updateResidentCashPayerStatus(residentId, data);
    },
    onSuccess: () => {
      return Promise.all([
        invalidateResident(queryClient, residentId, nursingHomeId),
        invalidateResidentsKeys(queryClient, nursingHomeId),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
      ]);
    },
  });
};

export const useNursingHome = (
  nursingHomeId: string | undefined
): UseQueryResult<NursingHomeDto, unknown> => {
  const selectNursingHome = useCallback(
    (data: NursingHomeDto) => ({
      ...data,
      paymentsDisabledUntil:
        data.paymentsDisabledUntil && new Date(data.paymentsDisabledUntil),
    }),
    []
  );

  return useApiQuery(
    useResidentApi,
    NURSING_HOME_KEY(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw Error('nursingHomeId is undefined');
      }

      return api.getNursingHome(nursingHomeId);
    },
    {
      enabled: nursingHomeId !== undefined,
      select: selectNursingHome,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnMount: false,
    }
  );
};

export const usePatchNursingHomePaymentSettings = (
  nursingHomeId: string | undefined
): UseMutationResult<
  {
    success: boolean;
  },
  unknown,
  UpdateNursingHomePaymentSettingsDto
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  const { user } = useAuthentication();

  return useMutation<
    {
      success: boolean;
    },
    unknown,
    UpdateNursingHomePaymentSettingsDto
  >({
    mutationKey: ['PATCH_NURSING_HOME_PAYMENT_SETTINGS', nursingHomeId],
    mutationFn: (data) => {
      if (!nursingHomeId) {
        throw Error('nursingHomeId is undefined');
      }
      return api.patchNursingHomePaymentSettings({
        params: {
          nursingHomeId: nursingHomeId,
        },
        body: data,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: NURSING_HOME_KEY(nursingHomeId),
      });
      // We need to invalidate the user profile because it fetches the nursing home data, and e.g. the indicator on the
      // side menu (that shows if payments are deactivated) depends on that nursing home data it fetches.
      await queryClient.invalidateQueries({
        queryKey: USER_PROFILE_KEY(user?.userId ?? ''),
      });
    },
  });
};

export const useUpdateNursingHome = (): UseMutationResult<
  NursingHomeDto,
  unknown,
  UpdateNursingHomeDto
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<NursingHomeDto, unknown, UpdateNursingHomeDto>({
    mutationKey: ['UPDATE_NURSING_HOME'],
    mutationFn: (data) => api.updateNursingHome(data),
    onSuccess: (result) => {
      return queryClient.invalidateQueries({
        queryKey: NURSING_HOME_KEY(result.id),
      });
    },
  });
};

export const useProceedToAwaitingSettlement = (
  residentId: string
): UseMutationResult<void, unknown, void> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationKey: ['PROCEED_TO_AWAITING_SETTLEMENT', residentId],
    mutationFn: () => api.proceedToAwaitingSettlement(residentId),
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RESIDENT_SETTLEMENT_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PHOENIX_KEY(),
        }),
      ]);
    },
  });
};

export const useGoBackToExited = (
  residentId: string
): UseMutationResult<void, unknown, void> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationKey: ['GO_BACK_TO_EXITED', residentId],
    mutationFn: () => api.goBackToExited(residentId),
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RESIDENT_SETTLEMENT_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PHOENIX_KEY(),
        }),
      ]);
    },
  });
};

export const useExitResident = (
  residentId: string
): UseMutationResult<void, unknown, ExitResidentDto> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, ExitResidentDto>({
    mutationKey: [EXIT_RESIDENT_KEY],
    mutationFn: (data) =>
      api.exitResident({
        body: data,
        params: { residentId },
      }),
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RESIDENT_SETTLEMENT_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PHOENIX_KEY(),
        }),
      ]);
    },
  });
};

export const useResidentSettlement = (
  residentId: string,
  options?: Omit<
    UseQueryOptions<SettlementDto, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<SettlementDto, unknown> => {
  return useApiQuery(
    useResidentApi,
    RESIDENT_SETTLEMENT_KEY(residentId),
    (api) =>
      api.getResidentSettlement({
        params: { residentId },
        body: undefined,
      }),
    options
  );
};

export const useArchiveResident = (
  residentId: string,
  nursingHomeId?: string
): UseMutationResult<void, unknown, SettlementFormDataDto> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, SettlementFormDataDto>({
    mutationKey: [ARCHIVE_RESIDENT_KEY],
    mutationFn: async (body) => {
      await api.archiveResident({
        body,
        params: { residentId },
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RESIDENT_SETTLEMENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: getTransactionsKey(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PAGINATED_KEY({
            nursingHomeId: nursingHomeId ?? '',
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(nursingHomeId),
        }),
        invalidateAllResidentBalance(queryClient, nursingHomeId),
        invalidateCashList(queryClient, nursingHomeId),
        invalidateResident(queryClient, residentId, nursingHomeId),
      ]);
    },
  });
};

export const useGetServiceProviderListForResident = (
  residentId: string,
  options?: Omit<
    UseQueryOptions<ServiceProviderListDto[], unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ServiceProviderListDto[], unknown> => {
  return useApiQuery(
    useResidentApi,
    RESIDENT_SERVICE_PROVIDERS_LIST_KEY(residentId),
    (api) =>
      api.getServiceProviderListForResident({
        params: { residentId },
      }),
    options
  );
};

export const usePutResidentServiceProvidersList = ({
  residentId,
}: {
  residentId: string;
}): UseMutationResult<
  {
    success: boolean;
  },
  unknown,
  PutResidentServiceProvidersDto
> => {
  const api = useResidentApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      success: boolean;
    },
    unknown,
    PutResidentServiceProvidersDto
  >({
    mutationKey: [PUT_RESIDENT_SERVICE_PROVIDERS_LIST_KEY],
    mutationFn: (data) =>
      api.putServiceProviderListForResident({
        params: {
          residentId,
        },
        body: data,
      }),
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: RESIDENT_SERVICE_PROVIDERS_LIST_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
      ]);
    },
  });
};
